/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import mixins from '../assets/styles/mixins.scss';

/**
 * Status Color Utility
 *
 * Simple utility to get colors for status IDs
 */

// Status ID to color mapping
const STATUS_COLORS = {
  // Assignment statuses
  completed: {
    backgroundColor: mixins.accentColor,
    textColor: mixins.accentGreen,
    dotColor: mixins.accentGreen,
    progressColor: mixins.amethyst,
    badgeColor: mixins.accentGreen,
    completionTextColor: mixins.zambezi
  },
  'completed-dark': {
    backgroundColor: mixins.darkCompletedBgColor,
    textColor: mixins.darkCompletedTextColor,
    dotColor: mixins.darkCompletedTextColor,
    progressColor: mixins.darkGreatBgColor
  },
  inprogress: {
    backgroundColor: mixins.accentLightOrange,
    textColor: mixins.accentOrange,
    dotColor: mixins.accentOrange,
    progressColor: mixins.progressInprogressColor,
    badgeColor: mixins.accentOrange,
    completionTextColor: mixins.zambezi
  },
  'inprogress-dark': {
    backgroundColor: mixins.darkInProgressBgColor,
    textColor: mixins.darkInProgressTextColor,
    dotColor: mixins.darkInProgressTextColor,
    progressColor: mixins.accentYellow
  },
  notstarted: {
    backgroundColor: mixins.accentLightRed,
    textColor: mixins.timeIconColor,
    dotColor: mixins.accentRed,
    progressColor: mixins.accentNotAttempted,
    badgeColor: mixins.accentRed,
    completionTextColor: mixins.zambezi
  },
  'notstarted-dark': {
    backgroundColor: mixins.darkNotStartedBgColor,
    textColor: mixins.darkNotStartedTextColor,
    dotColor: mixins.darkNotStartedTextColor
  },
  // Performance statuses
  great: {
    backgroundColor: mixins.accentColor,
    textColor: mixins.accentGreen,
    dotColor: mixins.accentGreenDark,
    progressColor: mixins.accentGreenDark,
    badgeColor: mixins.accentGreen
  },
  'great-dark': {
    backgroundColor: mixins.darkGreatBgColor,
    textColor: mixins.accentGreen,
    dotColor: mixins.darkGreatBgColor
  },
  gettingthere: {
    backgroundColor: mixins.accentLightOrange,
    textColor: mixins.accentOrange,
    dotColor: mixins.accentYellow,
    progressColor: mixins.accentYellow,
    badgeColor: mixins.accentOrange
  },
  'gettingthere-dark': {
    backgroundColor: mixins.darkGettingThereBgColor,
    textColor: mixins.yellowishOrange,
    dotColor: mixins.darkGettingThereBgColor
  },
  okay: {
    backgroundColor: mixins.accentLightOrange,
    textColor: mixins.accentOrange,
    dotColor: mixins.accentYellow,
    badgeColor: mixins.yellowishOrange
  },
  'okay-dark': {
    backgroundColor: mixins.darkGettingThereBgColor,
    textColor: mixins.yellowishOrange,
    dotColor: mixins.darkGettingThereBgColor
  },
  needsreview: {
    backgroundColor: mixins.accentLightRed,
    textColor: mixins.accentRed,
    dotColor: mixins.accentRedDark,
    progressColor: mixins.accentRedDark,
    badgeColor: mixins.accentRed
  },
  'needsreview-dark': {
    backgroundColor: mixins.darkNeedsReviewBgColor,
    textColor: mixins.accentRed,
    dotColor: mixins.darkNeedsReviewBgColor
  },
  completedPastDue: {
    backgroundColor: mixins.lightGreyBgColor,
    textColor: mixins.blueColor,
    dotColor: mixins.blueColor,
    progressColor: mixins.purpleSelectedTabUnderline,
    badgeTooltipColor: mixins.blueColor,
    badgeColor: mixins.blueColor,
    completionTextColor: mixins.zambezi
  },

  // Performance statuses
  notAttempted: {
    backgroundColor: mixins.accentNotAttempted,
    textColor: mixins.accentNotAttempted,
    dotColor: mixins.accentNotAttempted
  },
  'n/a': {
    backgroundColor: mixins.naBGColor,
    textColor: mixins.blueColor,
    dotColor: mixins.blueColor,
    badgeTooltipColor: mixins.accentRed,
    badgeColor: mixins.blueColor
  },
  questionstotal: {
    textColor: mixins.color_text_primary
  },
  completedOnTime: {
    textColor: mixins.accentGreen,
    badgeTooltipColor: mixins.accentGreen
  },
  topicStyles: {
    backgroundColor: mixins.feedBackColor,
    badgeColor: mixins.accentRed
  },
  notrelevant: {
    backgroundColor: mixins.accentLightRed,
    textColor: mixins.accentRed,
    badgeColor: mixins.accentRed
  },
  notcorrect: {
    backgroundColor: mixins.feedBackColor,
    textColor: mixins.timeIconColor,
    badgeColor: mixins.timeIconColor
  },
  unsafe: {
    backgroundColor: mixins.feedBackColor,
    textColor: mixins.timeIconColor,
    badgeColor: mixins.timeIconColor
  },
  somethingelse: {
    backgroundColor: mixins.accentLightRed,
    textColor: mixins.accentRed,
    badgeColor: mixins.accentRed
  },
  needsWork: {
    backgroundColor: mixins.accentLightRed,
    textColor: mixins.accentRed
  },
  active: {
    backgroundColor: mixins.accentColor,
    textColor: mixins.accentGreen,
    badgeColor: mixins.accentGreen
  },
  inactive: {
    backgroundColor: mixins.accentLightRed,
    textColor: mixins.accentRed,
    badgeColor: mixins.accentRed
  },
  student: {
    backgroundColor: mixins.feedBackColor,
    textColor: mixins.amethystColor,
    badgeColor: mixins.amethystColor
  },
  ta: {
    backgroundColor: mixins.naBGColor,
    textColor: mixins.blueColor,
    badgeColor: mixins.blueColor
  },
  instructor: {
    backgroundColor: mixins.bulkEditCtaButtonDisabledBGColor,
    textColor: mixins.timeIconColor,
    badgeColor: mixins.timeIconColor
  }
};

/**
 * Gets color configuration for a status ID
 */
export const getStatusColors = (statusId) => STATUS_COLORS[statusId] || STATUS_COLORS.notstarted;

/**
 * Gets background color for a status ID
 */
export const getBackgroundColor = (statusId) => getStatusColors(statusId).backgroundColor;

/**
 * Gets text color for a status ID
 */
export const getTextColor = (statusId) => getStatusColors(statusId).textColor;

/**
 * Gets completion tooltip text color for a status ID
 */
export const getCompletionTooltipTextColor = (statusId) => getStatusColors(statusId).completionTextColor;

/**
 * Gets dot color for a status ID
 */
export const getDotColor = (statusId) => getStatusColors(statusId).dotColor;

/**
 * Gets progress color for a status ID
 */
export const getProgressColor = (statusId) => getStatusColors(statusId).progressColor;

/**
 * Gets BadgeTooltip color for a status ID
 */
export const getBadgeTooltipColor = (statusId) => getStatusColors(statusId).badgeTooltipColor;

/**
 * Gets inline styles for a status ID
 */
export const getStatusStyles = (statusId, options = {}) => {
  const colors = getStatusColors(statusId);
  const { includeBorder = false, showBullet = false } = options;

  return {
    backgroundColor: colors.backgroundColor,
    color: showBullet ? colors.progressColor : colors.badgeColor,
    dotColor: colors.dotColor,
    ...(includeBorder && {
      border: `1px solid ${colors.badgeColor}`,
      borderRadius: '4px'
    })
  };
};

/**
 * Gets all available status IDs
 */
export const getAvailableStatusIds = () => Object.keys(STATUS_COLORS);

// Default export
export default {
  getStatusColors,
  getBackgroundColor,
  getTextColor,
  getDotColor,
  getStatusStyles,
  getAvailableStatusIds,
  getCompletionTooltipTextColor
};
